import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';

import { Button } from '@llura/ui';
import { AnalyticsEvents, AnalyticsProps } from '@llura/constants';

import { GenderStep } from './steps/gender-step';
import { ToneStep } from './steps/tone-step';
import { LookStep } from './steps/look-step';
import { CovenantStep } from './steps/covenant-step';
import { GeneratingStep } from './steps/generating-step';
import { RevealStep } from './steps/reveal-step';
import {
  buildDailyCommand,
  pickCoachName,
} from '../../lib/coach-persona';
import { useCoachStore, type CoachGender, type CoachTone } from '../../stores/coach.store';
import { useIntroStore } from '../../stores/intro.store';
import { useHaptic } from '../../hooks/useHaptic';
import { useStrings } from '../../hooks/useStrings';
import { useScrollToTop } from '../../hooks/use-scroll-to-top';
import { trackEvent } from '../../lib/analytics';

type Step = 'gender' | 'tone' | 'look' | 'covenant' | 'generating' | 'reveal';

const STEPS_WITH_BACK: readonly Step[] = ['gender', 'tone', 'look', 'covenant'];

export function CoachConfiguratorPage() {
  const navigate = useNavigate();
  const configure = useCoachStore((s) => s.configure);
  const haptic = useHaptic();
  const { strings } = useStrings();
  const introCompleted = useIntroStore((s) => s.completed);

  const [step, setStep] = useState<Step>('gender');
  const [gender, setGender] = useState<CoachGender | null>(null);
  const [tone, setTone] = useState<CoachTone | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useScrollToTop(step);

  // Stable seed derived from picks so we don't reshuffle name/avatar between
  // re-renders of the reveal step.
  const seed = useMemo(() => `${gender ?? ''}-${tone ?? ''}-${Date.now()}`, [gender, tone]);

  const generated = useMemo(() => {
    if (!gender || !tone || !avatarUrl) return null;
    return {
      name: pickCoachName(gender, tone, seed),
      avatarUrl,
      dailyCommand: buildDailyCommand(strings, tone, seed),
    };
  }, [gender, tone, avatarUrl, seed, strings]);

  // Direct deeplinks into /onboarding/coach without seeing the intro first
  // are bounced — the safe-word setup lives in the intro now and is a
  // prerequisite for the coach flow to feel honest.
  useEffect(() => {
    if (!introCompleted) navigate('/intro', { replace: true });
  }, [introCompleted, navigate]);

  if (!introCompleted) return <Navigate to="/intro" replace />;

  const back = () => {
    haptic.tap();
    if (step === 'covenant') setStep('look');
    else if (step === 'look') setStep('tone');
    else if (step === 'tone') setStep('gender');
    else if (step === 'gender') navigate('/intro');
  };

  const confirmCoach = () => {
    if (!gender || !tone || !generated) return;
    haptic.success();
    configure({
      gender,
      tone,
      name: generated.name,
      avatarUrl: generated.avatarUrl,
      dailyCommand: generated.dailyCommand,
    });
    trackEvent(AnalyticsEvents.CoachConfigured, {
      [AnalyticsProps.Gender]: gender,
      [AnalyticsProps.Tone]: tone,
      [AnalyticsProps.CoachName]: generated.name,
    });
    navigate('/onboarding/signup');
  };

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        {STEPS_WITH_BACK.includes(step) && (
          <Button
            variant="ghost"
            size="icon"
            onClick={back}
            className="rounded-full"
            aria-label={strings.common.back}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="mx-auto w-full max-w-md px-5 pb-16 pt-4">
        <AnimatePresence mode="wait">
          {step === 'gender' && (
            <GenderStep
              key="gender"
              value={gender}
              onSelect={(g) => {
                haptic.tap();
                setGender(g);
                setTimeout(() => setStep('tone'), 220);
              }}
            />
          )}
          {step === 'tone' && (
            <ToneStep
              key="tone"
              value={tone}
              onSelect={(t) => {
                haptic.tap();
                setTone(t);
                setTimeout(() => setStep('look'), 220);
              }}
            />
          )}
          {step === 'look' && gender && (
            <LookStep
              key="look"
              gender={gender}
              value={avatarUrl}
              onSelect={(url) => {
                haptic.tap();
                setAvatarUrl(url);
                setTimeout(() => setStep('covenant'), 220);
              }}
            />
          )}
          {step === 'covenant' && (
            <CovenantStep
              key="covenant"
              onConfirm={() => {
                haptic.tap();
                setStep('generating');
              }}
            />
          )}
          {step === 'generating' && (
            <GeneratingStep key="generating" onDone={() => setStep('reveal')} />
          )}
          {step === 'reveal' && gender && tone && generated && (
            <RevealStep
              key="reveal"
              gender={gender}
              tone={tone}
              name={generated.name}
              avatarUrl={generated.avatarUrl}
              onConfirm={confirmCoach}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
